import React from "react";
import { Check, Package, ShoppingBag, PartyPopper } from "lucide-react";
import type { ConfirmationModalProps } from "./types";

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  show,
  orderResponse,
  onTrackOrder,
  onContinueShopping,
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-neutral-900 rounded-3xl max-w-md w-full shadow-2xl overflow-hidden border border-neutral-800 animate-in zoom-in-95 duration-300">
        {/* Success Animation */}
        <div className="relative overflow-hidden bg-green-500/10 p-8">
          <div className="absolute top-4 right-4">
            <PartyPopper className="w-8 h-8 text-green-400 animate-bounce" />
          </div>
          <div className="text-center">
            <div className="relative inline-block mb-4">
              <div className="absolute inset-0 bg-green-500 rounded-full blur-2xl opacity-30 animate-pulse"></div>
              <div className="relative w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto shadow-xl">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <Check className="w-10 h-10 text-white stroke-3" />
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-white mb-2">
              Order Confirmed!
            </h2>
            <p className="text-green-400 font-semibold text-lg">
              Thank you for your purchase! 🎉
            </p>
          </div>
        </div>

        <div className="p-6">
          {/* Display order details if available */}
          {orderResponse ? (
            <div className="mb-6">
              {orderResponse.message && (
                <p className="text-neutral-400 mb-4 text-center leading-relaxed">
                  {orderResponse.message}
                </p>
              )}

              {orderResponse.order_id && (
                <div className="bg-neutral-800 rounded-2xl p-5 mb-4 border border-neutral-700">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-neutral-400 font-medium flex items-center gap-2">
                        <Package className="w-4 h-4 text-primary" />
                        Order ID
                      </span>
                      <span className="font-bold text-primary text-lg">
                        #{orderResponse.order_id}
                      </span>
                    </div>
                    {orderResponse.items_count && (
                      <div className="flex items-center justify-between">
                        <span className="text-neutral-400 font-medium">
                          Items
                        </span>
                        <span className="font-semibold text-white">
                          {orderResponse.items_count}
                        </span>
                      </div>
                    )}
                    {orderResponse.total_usd_price && (
                      <div className="flex items-center justify-between border-t border-neutral-700 pt-3">
                        <span className="text-neutral-300 font-bold text-lg">
                          Total
                        </span>
                        <span className="font-bold text-green-400 text-xl">
                          ${orderResponse.total_usd_price.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-neutral-400 mb-6 text-center">
              Thank you for your purchase!
            </p>
          )}

          <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 mb-6">
            <p className="text-sm text-blue-300 text-center font-medium">
              📦 Your order is being processed and will be shipped soon!
            </p>
            <p className="text-xs text-blue-400/70 text-center mt-1">
              Track your order status in your account
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={onTrackOrder}
              className="w-full bg-primary text-black py-4 rounded-xl font-bold hover:bg-primary-dark transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Track Your Order
            </button>
            <button
              onClick={onContinueShopping}
              className="w-full border border-neutral-700 text-neutral-300 py-4 rounded-xl font-bold hover:bg-neutral-800 hover:border-neutral-600 transition-all duration-200 shadow-sm flex items-center justify-center gap-2"
            >
              <ShoppingBag className="w-5 h-5" />
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
